import React from 'react'
import { useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { isEmpty } from 'lodash'
import { selectHomeValue } from 'src/store/selectors/home/metadata'
import { If, Unless } from 'src/Components/shared/Conditionals/Conditionals'
import classNames from 'classnames'
import styles from './EquitySmartSearchTable.module.scss'
import Text from 'src/Components/shared/Text'
import { selectPriceBrackets } from 'store/selectors/home/equity'
import { selectShouldRenderEquitySmartSearchFeatures } from 'src/store/selectors/display'
import Format from 'src/Components/shared/Format'
import { IconTilde } from 'src/Components/shared/Icon'

import Button from 'src/Components/shared/Button'
import { OVERLAY_HELPERS } from 'constants/navigation'
import history from 'src/history'
import { EquitySmartSearchCalculationClick } from 'src/constants/trackingEvents'

const Label = props => (
  <Text size='s' element='div' color='heather' {...props} />
)

const EquitySmartSearchTable = () => {
  const brackets = useSelector(selectPriceBrackets)
  const homeValue = useSelector(selectHomeValue)
  const showEquitySmartSearchFeatures = useSelector(
    selectShouldRenderEquitySmartSearchFeatures
  )

  const handleNavigateToCalculation = () => {
    history.push(OVERLAY_HELPERS.equitySmartSearch.buildPath())
  }

  return (
    <If condition={showEquitySmartSearchFeatures}>
      <Unless condition={isEmpty(brackets)}>
        <section className={styles.header} data-qa='equity-smart-search-table'>
          <Text
            element='h4'
            color='heather'
            size='xs'
            weight='semibold'
            className={styles.title}
          >
            <FormattedMessage
              id='MarketSpotLight.equitySmartSearch'
              defaultMessage='Equity Smart Search'
            />
          </Text>
          <Text
            element='p'
            size='l'
            align='center'
            paragraph
            className={styles.cta}
          >
            <FormattedMessage
              id='MarketSpotLight.equitySmartSearch.cta'
              defaultMessage='What if you sold your home for {homeValue}, pocketed some equity, and downsized to a new home nearby?'
              values={{
                homeValue: (
                  <Text element='span' color='pink'>
                    <Format.Currency value={homeValue} abbreviate />
                  </Text>
                )
              }}
            />
          </Text>
        </section>

        {brackets.map(bracket => {
          return (
            <section key={bracket.purchasePrice} className={styles.wrapper}>
              <div className={styles.wrapperItem}>
                <Label>
                  <FormattedMessage
                    id='MarketSpotLight.equitySmartSearch.buyPrice'
                    defaultMessage='Buy a new home for'
                  />
                  <Text size='s' color='white' element='div' weight='semibold'>
                    <Format.Currency value={bracket.purchasePrice} abbreviate />
                  </Text>
                </Label>
              </div>
              <div className={styles.wrapperItem}>
                <Label>
                  <If condition={bracket.mortgagePaymentDifference < 0}>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearch.paymentGoDown'
                      defaultMessage='Payment could go down'
                    />
                    <Text
                      size='s'
                      color='green'
                      element='div'
                      weight='semibold'
                    >
                      <div className={styles.tildeWrapper}>
                        <div
                          className={styles.paymentTilde}
                          aria-hidden='true'
                          title='about'
                        >
                          <IconTilde />
                        </div>
                        <span className={styles.srOnly}>about</span>
                        <Format.Currency
                          value={Math.abs(bracket.mortgagePaymentDifference)}
                          abbreviate
                        />
                        {'/mo'}
                      </div>
                    </Text>
                  </If>
                  <If condition={bracket.mortgagePaymentDifference >= 0}>
                    <FormattedMessage
                      id='MarketSpotLight.equitySmartSearch.paymentGoUp'
                      defaultMessage='Payment could go up'
                    />
                    <Text size='s' color='red' element='div' weight='semibold'>
                      <div className={styles.tildeWrapper}>
                        <span
                          className={styles.paymentTilde}
                          aria-hidden='true'
                          title='about'
                        >
                          <IconTilde />
                        </span>
                        <span className={styles.srOnly}>about</span>
                        <Format.Currency
                          value={bracket.mortgagePaymentDifference}
                          abbreviate
                        />
                        {'/mo'}
                      </div>
                    </Text>
                  </If>
                </Label>
              </div>

              <div
                className={classNames(
                  styles.wrapperItem,
                  styles.wrapperItemSpecial
                )}
              >
                <div className={styles.pocketAmountWrapper}>
                  <div className={styles.pocketAmountBox}>
                    <Text color='green' size='tiny' element='div'>
                      <FormattedMessage
                        id='MarketSpotLight.equitySmartSearch.pocket'
                        defaultMessage='+POCKET'
                      />
                    </Text>
                    <Text
                      element='div'
                      color='green'
                      size='xl'
                      className={styles.pocketWrapper}
                    >
                      <span
                        className={styles.pocketTilde}
                        aria-hidden='true'
                        title='about'
                      >
                        <IconTilde />
                      </span>
                      <span className={styles.srOnly}>about</span>
                      <Format.Currency
                        value={bracket.pocketAmount}
                        abbreviate
                      />
                    </Text>
                  </div>
                </div>
              </div>
            </section>
          )
        })}
        <Button
          className={styles.calculateButton}
          track={EquitySmartSearchCalculationClick}
          onClick={handleNavigateToCalculation}
        >
          <FormattedMessage
            data-qa='equitySmartSearchCalculationButton'
            id='MarketSpotLight.equitySmartSearch.howIsThisCalculated'
            defaultMessage='How is this calculated?'
          />
        </Button>
      </Unless>
    </If>
  )
}

export default EquitySmartSearchTable
